/**
 * Parses a failed response's body and derives the server's
 * reason from it in one pass. A `Response` body reads once, so
 * a caller wanting both takes them together from here.
 *
 * `reason` is empty when the body carries no string `error`
 * field and HTTP/2 has left `statusText` blank, leaving the
 * caller to decide what to say instead. `body` is null when
 * the response carried none, or it did not parse as JSON.
 */
export async function parseApiErrorResponse(
  response: Response
): Promise<{ reason: string; body: unknown }> {
  const body = await parseErrorResponseBody(response);

  return { reason: errorReasonFromBody(body, response), body };
}

async function parseErrorResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => null);
  }

  const text = await response.text().catch(() => '');
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function errorReasonFromBody(body: unknown, response: Response): string {
  if (
    body &&
    typeof body === 'object' &&
    'error' in body &&
    typeof (body as { error: unknown }).error === 'string'
  ) {
    const message = (body as { error: string }).error.trim();
    if (message) {
      return message;
    }
  }

  return response.statusText?.trim() ?? '';
}
